/**
 * Decision 0102: the card escalation chain. An agent's card goes to the agent above it first: a role or worker agent's
 * card to the area lead of the item's area (else the project lead), an area lead's card to the project lead. That
 * agent decides it within its own authority, answers it, or escalates it one level up with its recommendation; the
 * project lead raises it to the person. Person-only cards (outward, permission, project requests, monitors, memory
 * about a person, page adoptions, review picks, and plan cards a policy rule reserves for a person) skip the area lead:
 * the project lead may only add her recommendation and raise them. Decision 0134 (0133 §3): taint is recorded on a
 * card but no longer routes it; (0133 §4) Merge is a person's click only on a human review stage, which is a person's
 * card by construction (`openHumanReview`), never a chain card. Pure.
 */
import { agentOf, itemOf, leadOf, memoryOf } from "./state"
import type {
  Actor,
  Agent,
  AgentId,
  Approver,
  AskedRule,
  GraphCommand,
  ProjectState,
  Proposal,
  UserId,
} from "./types"
import { areaLeadOf } from "./workspace-pages"

/**
 * The asked rules that reserve a plan card for a person (`forcedApproverOf`): no agent decides them. Decision 0138
 * (lessons 1–4 §3, rule-made cards): only what is someone's own stays here (another keeper's agent, an agent's level, a
 * person's item and the description they wrote). The brief, Ask first and frozen checks are rules a policy can grant
 * (`revise_brief`, `revise_checks{frozen}`): the agent above decides such a card when its own policy allows the change.
 */
const PERSON_RULES: ReadonlySet<AskedRule> = new Set<AskedRule>([
  "cross_owner",
  "agent_level",
  "person_item",
  "person_spec",
])

/** Option effects only a person applies: page adoptions and review picks. */
const PERSON_EFFECTS: ReadonlySet<string> = new Set([
  "ChangeProjectSettings", // decision 0119: project settings are the owner's or an admin's
  "AdoptDocument",
  "AdoptWorkspaceDocument",
  "SetArtifactState",
  "ShareLogin", // 0128 addendum: only the login's owner shares it
  "RevisePolicy", // decision 0138: policies are the project admins' (a chat grant becomes a card they apply)
])

/**
 * Decision 0134 (0133 §1): a memory card is about a person when it changes personal memory (user scope) or a preference
 * about someone; one that reaches beyond the project (workspace scope, a promotion) or touches a locked note (the
 * draft's, or the existing note's when `s` is given) is a person's too. Any other memory card is the team's working
 * knowledge, which the lead decides.
 */
export const isPersonMemory = (
  b: Extract<Proposal["body"], { _tag: "memory" }>,
  s?: ProjectState,
): boolean => {
  const existing = s ? memoryOf(s, b.itemId) : undefined
  const scope = b.draft?.scope ?? b.fromScope ?? existing?.scope
  return (
    b.op === "promote" ||
    b.draft?.locked === true ||
    existing?.locked === true ||
    existing?.content._tag === "preference" ||
    b.draft?.content._tag === "preference" ||
    scope?._tag === "user" ||
    scope?._tag === "workspace"
  )
}

/**
 * A card no agent may decide: it still goes up the chain for the lead's recommendation, but only a person decides it.
 * Decision 0134 (0133 §3): taint no longer makes a card a person's.
 */
export const isPersonOnlyCard = (p: Pick<Proposal, "body" | "asked">, s?: ProjectState): boolean => {
  const b = p.body
  if (b._tag === "plan_change") return (p.asked ?? []).some((a) => PERSON_RULES.has(a.rule))
  if (b._tag === "question") return b.options.some((o) => o.effects.some((e) => PERSON_EFFECTS.has(e._tag)))
  if (b._tag === "memory") return isPersonMemory(b, s)
  return true
}

/** An agent that takes turns (paused and retired agents are skipped: the card goes one level further up). */
const active = (s: ProjectState, id: AgentId | null | undefined): Agent | undefined => {
  const a = id ? agentOf(s, id) : undefined
  return a?.status === "active" ? a : undefined
}

/** The project lead when she takes turns. */
const activeLead = (s: ProjectState): Agent | undefined => active(s, leadOf(s)?.id)

/** Does this agent lead an area (`areaLeadOf`) of the project? */
export const leadsAnArea = (s: ProjectState, agentId: AgentId): boolean =>
  s.areas.some((a) => a.status === "active" && areaLeadOf(s, a.id)?.id === agentId)

/** The item a change is about (an existing one, or the one it creates). */
const itemIdOfChange = (c: GraphCommand): string | null => {
  const x = c as { readonly itemId?: unknown }
  return typeof x.itemId === "string" ? x.itemId : null
}

/** The items a card is about: a question's subject, what it blocks, the items a plan change touches. */
export const cardItemIds = (p: Pick<Proposal, "body" | "blocks">): ReadonlyArray<string> => {
  const ids: string[] = []
  const b = p.body
  if (b._tag === "question" && b.subject?._tag === "item") ids.push(b.subject.id)
  if (b._tag === "plan_change") for (const c of b.changes) ids.push(itemIdOfChange(c) ?? "")
  for (const x of p.blocks) if (x._tag === "item") ids.push(x.id)
  return [...new Set(ids.filter((id) => id.length > 0))]
}

/** The area of the first item the card is about (an item's lane, else its parent's), else the proposer's area. */
const areaOfCard = (s: ProjectState, proposer: Agent, itemIds: ReadonlyArray<string>): string | null => {
  for (const id of itemIds) {
    let t = itemOf(s, id as never)
    for (let depth = 0; t && depth < 20; depth++) {
      if (t.areaId) return t.areaId
      t = t.parentId ? itemOf(s, t.parentId) : undefined
    }
  }
  return proposer.areaId ?? null
}

/**
 * The agent a new card from `proposer` is addressed to, or null (it goes to the person): the project lead's own cards
 * go to the person; a person-only card and an area lead's card go to the project lead; any other
 * agent's card to the area lead of the item's area (else the proposer's area), when that is another active agent, else
 * to the project lead. Where the next agent up does not take turns (paused, retired, none), the person.
 */
export const firstAddressee = (
  s: ProjectState,
  proposer: AgentId,
  p: Pick<Proposal, "body" | "blocks" | "asked">,
): AgentId | null => {
  const lead = activeLead(s)
  const me = agentOf(s, proposer)
  if (!me || lead?.id === proposer) return null
  if (isPersonOnlyCard(p, s) || leadsAnArea(s, proposer)) return lead?.id ?? null
  const areaId = areaOfCard(s, me, cardItemIds(p))
  const areaLead = areaId ? active(s, areaLeadOf(s, areaId as never)?.id) : undefined
  return areaLead && areaLead.id !== proposer ? areaLead.id : (lead?.id ?? null)
}

/** One level up from the agent holding a card: the project lead, or null (the person). */
export const nextAddressee = (s: ProjectState, holder: AgentId): AgentId | null => {
  const lead = activeLead(s)
  return lead && lead.id !== holder ? lead.id : null
}

/** Is this open card addressed to this agent? */
export const isAddressedTo = (p: Pick<Proposal, "approver">, agentId: string): boolean =>
  p.approver._tag === "agent" && p.approver.agentId === agentId

/** The open cards addressed to an agent, oldest first. */
export const cardsAddressedTo = (s: ProjectState, agentId: string): ReadonlyArray<Proposal> =>
  s.proposals.filter((p) => p.state === "open" && isAddressedTo(p, agentId))

/** The person a card ends with: its human approver, or the person an agent-addressed card escalates to. */
export const cardPersonOf = (p: Pick<Proposal, "approver">): UserId | null =>
  p.approver._tag === "human" ? p.approver.userId : p.approver._tag === "agent" ? p.approver.escalateTo : null

/**
 * The approver of a new card an agent opens: a card for a person goes to the agent above the proposer first
 * (`firstAddressee`), keeping that person as `escalateTo`. Decision 0117: a worker's card (its `remember`) goes to the
 * project lead, even when she owns the worker's item: a worker has no level of its own, so its card is never "the
 * lead's own card". People's, the system's and the Mayor's cards are unchanged.
 */
export const chainApprover = (
  s: ProjectState,
  actor: Actor,
  base: Approver,
  card: Pick<Proposal, "body" | "blocks" | "asked">,
): Approver => {
  if (base._tag !== "human") return base
  if (actor._tag === "worker") {
    const lead = activeLead(s)
    return lead ? { _tag: "agent", agentId: lead.id, escalateTo: base.userId } : base
  }
  if (actor._tag !== "agent") return base
  const to = firstAddressee(s, actor.agentId, card)
  return to ? { _tag: "agent", agentId: to, escalateTo: base.userId } : base
}

// decision 0117: memory cards that say the same thing

const MEMORY_REF_IN_TEXT = /\b([PM])-([1-9][0-9]*)\b/gi

/**
 * The other memory card (P-n) or memory (M-n) a note names, when it names one: the agent holding a memory card may
 * drop it as a duplicate of that. Null when the note names none (or only the card itself).
 */
export const duplicateNamed = (s: ProjectState, p: Pick<Proposal, "id">, note: string): string | null => {
  for (const m of note.matchAll(MEMORY_REF_IN_TEXT)) {
    const ref = `${(m[1] ?? "").toUpperCase()}-${m[2]}`
    if (ref.startsWith("P")) {
      const other = s.proposals.find((x) => x.ref === ref)
      if (other && other.id !== p.id && other.body._tag === "memory") return ref
    } else if (s.memory.some((x) => x.ref === ref)) return ref
  }
  return null
}

const STOP = new Set([
  "that",
  "this",
  "with",
  "from",
  "when",
  "then",
  "than",
  "they",
  "there",
  "their",
  "into",
  "only",
])

const wordsOf = (text: string): ReadonlySet<string> =>
  new Set(
    text
      .toLowerCase()
      .split(/[^a-z0-9@._-]+/)
      .map((w) => w.replace(/^[._-]+|[._-]+$/g, ""))
      .filter((w) => w.length >= 4 && !STOP.has(w)),
  )

/** Share of the smaller word set found in the other (0…1). */
const overlap = (a: ReadonlySet<string>, b: ReadonlySet<string>): number => {
  if (a.size === 0 || b.size === 0) return 0
  let common = 0
  for (const w of a) if (b.has(w)) common++
  return common / Math.min(a.size, b.size)
}

/** At or above this share of shared title words, two memory notes are about the same thing. */
export const MEMORY_OVERLAP = 0.5

const memoryTitleOf = (p: Proposal): string => (p.body._tag === "memory" ? (p.body.draft?.title ?? "") : "")

/**
 * Decision 0117: the open memory cards and accepted memories a memory card overlaps (the same memory item, or at least
 * MEMORY_OVERLAP of the shorter title's words), as refs, at most three. Shown to the agent triaging it, so it drops the
 * duplicate or says which one to keep.
 */
export const overlappingMemory = (s: ProjectState, p: Proposal): ReadonlyArray<string> => {
  if (p.body._tag !== "memory") return []
  const itemId = p.body.itemId
  const mine = wordsOf(memoryTitleOf(p))
  const out: string[] = []
  for (const x of s.proposals) {
    if (x.id === p.id || x.body._tag !== "memory" || !x.ref) continue
    if (x.state !== "open" && x.state !== "revision_requested") continue
    if (x.body.itemId === itemId || overlap(mine, wordsOf(memoryTitleOf(x))) >= MEMORY_OVERLAP)
      out.push(x.ref)
  }
  for (const m of s.memory) {
    if (m.status !== "accepted" || !m.ref || m.supersededBy !== undefined) continue
    if (m.id === itemId || overlap(mine, wordsOf(m.title)) >= MEMORY_OVERLAP) out.push(m.ref)
  }
  return out.slice(0, 3)
}
